import React, { useEffect, useState, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx"; // ← NEW

/* helper */
const getUserId = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const d = jwtDecode(token);
    return d?.user?.userId || d?.userId || d?._id;
  } catch {
    return null;
  }
};

const Wallet = () => {
  const [wallet, setWallet] = useState(0);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ─ fetch once ─ */
  useEffect(() => {
    (async () => {
      const uid = getUserId();
      if (!uid) {
        setError("No valid user token");
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(
          `https://api.vyapakprint.com/api/distribution/${uid}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setWallet(data.wallet || 0);
        setTxns(
          (data.transactionHistory || []).sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          )
        );
      } catch (err) {
        console.error(err);
        setError("Failed to load wallet data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ─ export helper ─ */
  const exportToExcel = () => {
    const rows = txns.map((t) => ({
      Date: new Date(t.date).toLocaleString("en-IN"),
      Type: t.transactionType,
      Amount: t.amount,
      PreviousBalance: t.previousBalance,
      NewBalance: t.newBalance,
      Remark: t.remark,
      ReceiptID: t.receiptId,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, "wallet-transactions.xlsx");
  };

  if (loading) return <div className="p-6">Loading…</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  return (
    <div className="container mx-auto p-6 min-h-screen">
      {/* header */}
      <div className="mb-8 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <h1 className="text-3xl font-bold">Wallet</h1>
        <div className="flex gap-3">
          <Link to="/add-funds">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700">
              Add Funds
            </button>
          </Link>
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700"
          >
            Export&nbsp;to&nbsp;Excel
          </button>
        </div>
      </div>

      {/* balance */}
      <div className="bg-white p-6 rounded-lg shadow mb-6 border">
        <h2 className="text-xl font-semibold mb-4">Wallet Balance</h2>
        <p className="text-3xl font-bold text-green-600">
          ₹{wallet.toFixed(2)}
        </p>
        <p className="text-gray-600 mt-2">Total available balance</p>
      </div>

      {/* table */}
      <div className="bg-white p-6 rounded-lg shadow border overflow-auto">
        <h2 className="text-xl font-semibold mb-4">Transaction History</h2>

        <table className="min-w-[1000px] w-full text-sm text-center">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-3 px-4">Date &amp; Time</th>
              <th className="py-3 px-4">Type</th>
              <th className="py-3 px-4">Amount&nbsp;(₹)</th>
              <th className="py-3 px-4">Previous</th>
              <th className="py-3 px-4">New</th>
              <th className="py-3 px-4">Remark</th>
              <th className="py-3 px-4">Receipt&nbsp;ID</th>
            </tr>
          </thead>
          <tbody>
            {txns.length ? (
              txns.map((t) => (
                <tr key={t._id} className="border-t">
                  <td className="py-2 px-4">
                    {new Date(t.date).toLocaleString("en-IN")}
                  </td>
                  <td className="py-2 px-4 capitalize">{t.transactionType}</td>
                  <td
                    className={`py-2 px-4 font-semibold ${
                      t.transactionType === "credit"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {t.transactionType === "debit" ? "-" : "+"}
                    {t.amount.toFixed(2)}
                  </td>
                  <td className="py-2 px-4">{t.previousBalance.toFixed(2)}</td>
                  <td className="py-2 px-4">{t.newBalance.toFixed(2)}</td>
                  <td className="py-2 px-4">{t.remark || "—"}</td>
                  <td className="py-2 px-4">{t.receiptId || "—"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-6 text-gray-500">
                  No transactions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Wallet;
